import React, {useEffect, useState} from "react";
import axiosInstance from "../../utils/axiosInstance";
import {useNavigate, useParams} from "react-router-dom";
import {IoArrowBackSharp} from "react-icons/io5";

const EditAdditionalPrice = () => {
    const {id} = useParams();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        column: "ecu",
        value: "",
        credits: "",
        rule: "",
    });

    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchAdditionalPrice = async () => {
            setLoading(true);
            try {
                const response = await axiosInstance.get(`/additional-prices/${id}`);
                const priceData = response.data;

                setFormData({
                    column: priceData.column || "ecu",
                    value: priceData.value || "",
                    credits: priceData.credits?.toString() || "",
                    rule: priceData.rule || "",
                });
            } catch (err) {
                setError("Failed to load additional price.");
            } finally {
                setLoading(false);
            }
        };

        fetchAdditionalPrice();
    }, [id]);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            await axiosInstance.put(`/additional-prices/${id}`, {
                column: formData.column,
                value: formData.value,
                credits: parseFloat(formData.credits),
                rule: formData.rule,
            });
            navigate("/admin/additional-prices");
        } catch (err) {
            setError("Failed to update additional price. Please try again.");
            setLoading(false);
        }
    };

    return (
        <div className="mx-auto max-w-[1500px] space-y-5 px-4 pb-4 sm:px-6 sm:pb-6">
            <h3 className="mb-1 flex items-center gap-3 text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
                <button
                    onClick={() => window.history.back()}
                    className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-300 text-gray-500 transition hover:border-red-400/60 hover:text-red-500 dark:border-[#3a3a3d] dark:text-slate-400"
                >
                    <IoArrowBackSharp size={24}/>
                </button>
                <span className="h-6 w-1.5 shrink-0 rounded-full bg-gradient-to-b from-red-500 to-orange-400" />
                Edit Additional Price
            </h3>

            <form onSubmit={handleSubmit} className="p-6 rounded-[22px] border border-gray-200 bg-white shadow-lg shadow-gray-900/5 dark:border-[#2c2c2e] dark:bg-[#1c1c1e] dark:shadow-black/40">
                {/* Column */}
                <div className="mb-4">
                    <label htmlFor="column" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:text-slate-400">
                        Column
                    </label>
                    <select
                        id="column"
                        name="column"
                        value={formData.column}
                        onChange={handleChange}
                        className="h-11 w-full rounded-[12px] border border-gray-300 bg-white px-4 text-sm text-gray-800 outline-none transition placeholder:text-gray-400 focus:border-red-400 focus:ring-4 focus:ring-red-500/10 disabled:opacity-60 dark:border-[#3a3a3d] dark:bg-[#262628] dark:text-slate-100 dark:placeholder:text-slate-500"
                        required
                    >
                        <option value="">Select column</option>
                        <option value="ecu">ecu</option>
                    </select>
                </div>

                {/* Value */}
                <div className="mb-4">
                    <label htmlFor="value" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:text-slate-400">
                        Value
                    </label>
                    <input
                        type="text"
                        id="value"
                        name="value"
                        value={formData.value}
                        onChange={handleChange}
                        className="h-11 w-full rounded-[12px] border border-gray-300 bg-white px-4 text-sm text-gray-800 outline-none transition placeholder:text-gray-400 focus:border-red-400 focus:ring-4 focus:ring-red-500/10 disabled:opacity-60 dark:border-[#3a3a3d] dark:bg-[#262628] dark:text-slate-100 dark:placeholder:text-slate-500"
                        placeholder="e.g. edc16p"
                        required
                    />
                </div>

                <div className="mb-4">
                    <label htmlFor="price" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:text-slate-400">
                        Price
                    </label>
                    <input
                        type="number"
                        id="credits"
                        name="credits"
                        value={formData.credits}
                        onChange={handleChange}
                        className="h-11 w-full rounded-[12px] border border-gray-300 bg-white px-4 text-sm text-gray-800 outline-none transition placeholder:text-gray-400 focus:border-red-400 focus:ring-4 focus:ring-red-500/10 disabled:opacity-60 dark:border-[#3a3a3d] dark:bg-[#262628] dark:text-slate-100 dark:placeholder:text-slate-500"
                        required
                    />
                </div>

                <div className="mb-4">
                    <label
                        htmlFor="rule"
                        className="mb-1.5 block text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:text-slate-400"
                    >
                        Rule
                    </label>
                    <select
                        id="rule"
                        name="rule"
                        value={formData.rule}
                        onChange={handleChange}
                        className="h-11 w-full rounded-[12px] border border-gray-300 bg-white px-4 text-sm text-gray-800 outline-none transition placeholder:text-gray-400 focus:border-red-400 focus:ring-4 focus:ring-red-500/10 disabled:opacity-60 dark:border-[#3a3a3d] dark:bg-[#262628] dark:text-slate-100 dark:placeholder:text-slate-500"
                        required
                    >
                        <option value="">Select rule</option>
                        <option value="CONTAINS">CONTAINS</option>
                        <option value="STARTS_WITH">STARTS WITH</option>
                        <option value="EXACT_MATCH">EXACT MATCH</option>
                    </select>
                </div>

                {/* Error */}
                {error && <p className="mb-4 text-sm font-medium text-rose-600 dark:text-rose-400">{error}</p>}

                {/* Submit */}
                <div className="flex justify-end">
                    <button
                        type="submit"
                        className={`px-4 py-2 text-white rounded-md ${
                            loading ? "bg-gray-400" : "bg-gradient-to-r from-red-600 to-orange-500 hover:from-red-500 hover:to-orange-400"
                        }`}
                        disabled={loading}
                    >
                        {loading ? "Updating..." : "Update Additional Price"}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default EditAdditionalPrice;